"use client";

import { useState } from "react";
import Button from "@/components/ui/button";
import { uploadSpaceImage, uploadSpaceMedia } from "@/utils/supabaseStorage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faXmark } from "@fortawesome/free-solid-svg-icons";

export function ImageUploader({
  value,
  onChange,
  widgetId,
  spaceId,
  previewSize,
  acceptMedia = false,
  showPreview = true,
  children,
  className = "",
  multiple = false,
}) {
  const [isUploading, setIsUploading] = useState(false);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate all files have name property
    for (const file of files) {
      if (!file.name) {
        console.error("File object missing name property:", file);
        return;
      }
    }

    try {
      setIsUploading(true);

      if (acceptMedia) {
        // When acceptMedia is true, pass the file(s) directly to onChange
        if (multiple) {
          onChange(files); // Pass array of files
        } else {
          onChange(files[0]); // Pass single file
        }
      } else {
        // Default behavior - upload and return URL(s)
        if (multiple) {
          const uploadPromises = files.map((file) =>
            uploadSpaceImage(file, spaceId, widgetId)
          );
          const imageUrls = await Promise.all(uploadPromises);
          onChange(imageUrls); // Pass array of URLs
        } else {
          const imageUrl = await uploadSpaceImage(files[0], spaceId, widgetId);
          onChange(imageUrl); // Pass single URL
        }
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    } finally {
      setIsUploading(false);
      // Clear the input so the same file can be selected again
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  // If children are provided (custom trigger), render that
  if (children) {
    return (
      <label className={className}>
        <input
          type="file"
          className="hidden"
          accept={acceptMedia ? "image/*,video/*" : "image/*"}
          onChange={handleUpload}
          disabled={isUploading}
          multiple={multiple}
        />
        {children}
      </label>
    );
  }

  // If showPreview is false, don't render preview UI
  if (!showPreview) {
    return (
      <label className={className}>
        <input
          type="file"
          className="hidden"
          accept={acceptMedia ? "image/*,video/*" : "image/*"}
          onChange={handleUpload}
          disabled={isUploading}
          multiple={multiple}
        />
        <div className="flex items-center justify-center p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors cursor-pointer">
          <FontAwesomeIcon icon={faUpload} className="mr-2" />
          {isUploading
            ? "Uploading..."
            : `Upload ${multiple ? "Files" : "File"}`}
        </div>
      </label>
    );
  }

  const imageStyle = previewSize
    ? {
        width: `${previewSize}px`,
        height: `${previewSize}px`,
      }
    : {};

  return (
    <div className="w-full">
      {value ? (
        <div
          className={`relative ${
            previewSize ? "" : "h-[200px]"
          } rounded-2xl overflow-hidden group`}
          style={imageStyle}
        >
          <img src={value} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => onChange("")}
              className="!p-2 text-white hover:text-red-400"
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </Button>
            <label>
              <input
                type="file"
                className="hidden"
                accept={acceptMedia ? "image/*,video/*" : "image/*"}
                onChange={handleUpload}
                disabled={isUploading}
                multiple={multiple}
              />
              <Button
                variant="outline"
                className="!p-2 text-white cursor-pointer"
                disabled={isUploading}
              >
                <FontAwesomeIcon icon={faUpload} className="h-4 w-4" />
              </Button>
            </label>
          </div>
        </div>
      ) : (
        <label className="block">
          <div
            className={`flex flex-col items-center justify-center ${
              previewSize ? "" : "h-[75px]"
            } border-2 border-dashed rounded-2xl hover:border-primary cursor-pointer`}
            style={imageStyle}
          >
            <input
              type="file"
              className="hidden"
              accept={acceptMedia ? "image/*,video/*" : "image/*"}
              onChange={handleUpload}
              disabled={isUploading}
              multiple={multiple}
            />
            {isUploading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            ) : (
              <>
                <FontAwesomeIcon
                  icon={faUpload}
                  className="h-8 w-8 text-gray-400 mb-2"
                />
                <span className="text-sm text-gray-500">
                  Click to upload {acceptMedia ? "media" : "image"}
                  {multiple ? "s" : ""}
                </span>
              </>
            )}
          </div>
        </label>
      )}
    </div>
  );
}
